'use client';

import { useState, useEffect } from 'react';
import BreakingNewsAlert from './BreakingNewsAlert';
import CategoryFilter from './CategoryFilter';
import SearchBar from './SearchBar';
import NewsCard from './NewsCard';
import FeaturedArticle from './FeaturedArticle';
import NewsletterSignup from './NewsletterSignup';
import SocialMediaFeed from './SocialMediaFeed';

interface NewsArticle {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readTime: string;
  image: string;
  imageAlt: string;
  author: string;
  tags: string[];
  featured: boolean;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  count: number;
}

interface SocialPost {
  id: string;
  platform: string;
  content: string;
  timestamp: string;
  engagement: {
    likes: number;
    shares: number;
    comments: number;
  };
}

export default function NewsUpdatesInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories: Category[] = [
  { id: 'all', name: 'All News', icon: 'NewspaperIcon', count: 24 },
  { id: 'conservation', name: 'Conservation Efforts', icon: 'ShieldCheckIcon', count: 8 },
  { id: 'threats', name: 'Threats & Challenges', icon: 'ExclamationTriangleIcon', count: 5 },
  { id: 'success', name: 'Success Stories', icon: 'TrophyIcon', count: 6 },
  { id: 'research', name: 'Research & Data', icon: 'BeakerIcon', count: 3 },
  { id: 'community', name: 'Community Impact', icon: 'UserGroupIcon', count: 2 }];


  const mockArticles: NewsArticle[] = [];


  const socialPosts: SocialPost[] = [
  {
    id: '1',
    platform: 'Twitter',
    content: 'URGENT: Increased poaching activity detected in the northern sector. Rangers are responding. Your support helps us protect these magnificent creatures. #Rhinos&Rangers #ConservationAlert',
    timestamp: '2 hours ago',
    engagement: { likes: 342, shares: 89, comments: 45 }
  },
  {
    id: '2',
    platform: 'Facebook',
    content: 'Celebrating World Rhino Day with our community partners! Together, we have protected over 200 rhinos this year. Thank you for your unwavering support.',
    timestamp: '5 hours ago',
    engagement: { likes: 1205, shares: 234, comments: 156 }
  },
  {
    id: '3',
    platform: 'Instagram',
    content: 'Behind the scenes with our anti-poaching unit. These brave rangers work 24/7 to protect rhinos. Support their mission today. Link in bio.',
    timestamp: '1 day ago',
    engagement: { likes: 2341, shares: 456, comments: 289 }
  }];


  const filteredArticles = mockArticles.filter((article) => {
    const matchesCategory = activeCategory === 'all' || article.category === categories.find((c) => c.id === activeCategory)?.name;
    const matchesSearch = searchQuery === '' ||
    article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
    article.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const featuredArticle = mockArticles.find((article) => article.featured);
  const regularArticles = filteredArticles.filter((article) => !article.featured);

  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleReadMore = (articleId: string) => {
    console.log('Reading article:', articleId);
  };

  const handleNewsletterSubscribe = (email: string, frequency: string) => {
    console.log('Newsletter subscription:', email, frequency);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-16"></div>
        <div className="animate-pulse">
          <div className="h-96 bg-muted"></div>
          <div className="max-w-7xl mx-auto px-4 py-12">
            <div className="grid lg:grid-cols-4 gap-8">
              <div className="lg:col-span-3 space-y-8">
                <div className="h-64 bg-muted rounded-lg"></div>
                <div className="grid md:grid-cols-2 gap-6">
                  {[1, 2, 3, 4].map((i) =>
                  <div key={i} className="h-96 bg-muted rounded-lg"></div>
                  )}
                </div>
              </div>
              <div className="space-y-6">
                <div className="h-64 bg-muted rounded-lg"></div>
                <div className="h-96 bg-muted rounded-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="h-16"></div>

      <div className="text-center py-10">
        <h1 className="text-4xl font-headline font-bold text-foreground">Coming Soon</h1>
      </div>
      
      <BreakingNewsAlert
        message="URGENT: Major anti-poaching operation underway in Kruger National Park"
        link="#breaking-news"
        isVisible={true} />
      
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          <aside className="lg:col-span-1 space-y-6">
            <SearchBar
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              resultsCount={filteredArticles.length} />
            
            
            <CategoryFilter
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={handleCategoryChange} />
            
            
            <SocialMediaFeed posts={socialPosts} />
          </aside>
          
          <div className="lg:col-span-3 space-y-8">
            {featuredArticle && activeCategory === 'all' && searchQuery === '' &&
            <FeaturedArticle
              article={featuredArticle}
              onReadMore={handleReadMore} />

            }
            
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-headline font-bold text-foreground">
                  {activeCategory === 'all' ? 'Latest Articles' : categories.find((c) => c.id === activeCategory)?.name}
                </h2>
                <span className="text-sm text-muted-foreground font-body">
                  {filteredArticles.length} {filteredArticles.length === 1 ? 'article' : 'articles'}
                </span>
              </div>
              
              {regularArticles.length > 0 ?
              <div className="grid md:grid-cols-2 gap-6">
                  {regularArticles.map((article) =>
                <NewsCard
                  key={article.id}
                  article={article}
                  onReadMore={handleReadMore} />

                )}
                </div> :

              <div className="text-center py-12 bg-card rounded-lg border border-border">
                  <p className="text-muted-foreground font-body">
                    No articles found matching your criteria. Try adjusting your filters or search query.
                  </p>
                </div>
              }
            </div>
            
            <NewsletterSignup onSubscribe={handleNewsletterSubscribe} />
          </div>
        </div>
      </main>
    </div>);

}